import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import { Send } from 'lucide-react';
import { crmConversationsApi, crmMailboxesApi } from '../../api/crm';
import { useCompany } from '../../contexts/CompanyContext';
import { usePermissions } from '../../contexts/PermissionsContext';
import { invalidateCrm } from '../../lib/queryCache';
import Modal from '../Modal';
import Field from './Field';

interface Props {
  open: boolean;
  onClose: () => void;
  /** Pre-fill from a contact / organization / deal page. */
  defaults?: { to?: string; subject?: string; contactId?: string; organizationId?: string; dealId?: string };
}

/** Start a conversation by email — from the Inbox, or from a contact, deal
 *  or organization page. The reply lands back in the same thread. */
export default function ComposeEmailModal({ open, onClose, defaults }: Props) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentCompany } = useCompany();
  const { isAdmin } = usePermissions();
  const cid = (currentCompany as any)?.id ?? (currentCompany as any)?.uuid;

  const { data: mailboxes } = useQuery({
    queryKey: ['crm-mailboxes', cid],
    queryFn: () => crmMailboxesApi.list(),
    enabled: !!cid && open,
  });
  const active = (mailboxes || []).filter(m => m.isActive);

  const [form, setForm] = useState({ mailboxId: '', to: '', cc: '', subject: '', body: '' });
  const [error, setError] = useState('');
  useEffect(() => {
    if (!open) return;
    setForm({ mailboxId: '', to: defaults?.to ?? '', cc: '', subject: defaults?.subject ?? '', body: '' });
    setError('');
  }, [open, defaults?.to, defaults?.subject]);
  useEffect(() => {
    if (open && !form.mailboxId && active.length > 0) setForm(f => ({ ...f, mailboxId: active[0].id }));
  }, [open, active.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k: keyof typeof form, v: string) => setForm(f => ({ ...f, [k]: v }));

  const sendMutation = useMutation({
    mutationFn: () => crmConversationsApi.create({
      mailboxId: form.mailboxId, to: form.to, cc: form.cc || undefined, subject: form.subject, body: form.body,
      contactId: defaults?.contactId, organizationId: defaults?.organizationId, dealId: defaults?.dealId,
    }),
    onSuccess: (conversation) => {
      invalidateCrm(queryClient);
      toast.success('Email sent');
      onClose();
      navigate(`/crm/inbox/${conversation.id}`);
    },
    onError: (e: any) => setError(e.response?.data?.message ?? 'Could not send'),
  });

  const selected = active.find(m => m.id === form.mailboxId);
  const canSend = !!form.mailboxId && form.to.trim() && form.subject.trim() && form.body.trim() && !sendMutation.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="New email"
      width={640}
      busy={sendMutation.isPending}
      footer={
        <>
          <button type="button" className="btn btn-ghost" onClick={onClose} disabled={sendMutation.isPending}>Cancel</button>
          <button type="button" className="btn btn-primary inline-flex items-center gap-1.5" disabled={!canSend} onClick={() => sendMutation.mutate()}>
            <Send className="w-4 h-4" /> {sendMutation.isPending ? 'Sending…' : 'Send'}
          </button>
        </>
      }
    >
      {active.length === 0 ? (
        <p className="text-sm text-muted">
          No CRM mailbox is set up yet, so there is nothing to send from.{' '}
          {isAdmin
            ? <>Add one under <Link to="/settings/company" className="text-primary hover:underline">Company Settings → CRM</Link>.</>
            : 'Ask an admin to add one under Company Settings → CRM.'}
        </p>
      ) : (
        <div className="space-y-4">
          <Field label="From mailbox" required>
            <select className="select w-full" value={form.mailboxId} onChange={e => set('mailboxId', e.target.value)}>
              {active.map(m => <option key={m.id} value={m.id}>{m.name} — {m.address}</option>)}
            </select>
          </Field>
          <Field label="To" required hint="Separate several addresses with commas.">
            <input className="input w-full" value={form.to} onChange={e => set('to', e.target.value)} placeholder="name@company.com" autoFocus={!defaults?.to} />
          </Field>
          <Field label="Cc">
            <input className="input w-full" value={form.cc} onChange={e => set('cc', e.target.value)} placeholder="Optional" />
          </Field>
          <Field label="Subject" required>
            <input className="input w-full" value={form.subject} onChange={e => set('subject', e.target.value)} />
          </Field>
          <Field label="Message" required hint={selected?.settings.signature ? 'Your mailbox signature is added automatically.' : undefined}>
            <textarea className="input w-full min-h-[180px]" value={form.body} onChange={e => set('body', e.target.value)} />
          </Field>
          {error && <p className="text-sm text-err">{error}</p>}
        </div>
      )}
    </Modal>
  );
}
